export type NbaAudience = 'all' | 'brightside' | 'partner';
export type NbaOverrideMode = 'link' | 'popup';
export type NbaPublishMode = 'now' | 'schedule';
export type NbaAnnouncementStatus = 'none' | 'scheduled' | 'live';
export type NbaScenarioId = 'step1' | 'step2' | 'step3' | 'project' | 'webinar' | 'fallback' | 'override';
export type NbaPreviewType = 'step' | 'project' | 'webinar' | 'override' | 'fallback';

export const NBA_AUDIENCES: NbaAudience[] = ['all', 'brightside', 'partner'];

export interface NbaAnnouncement {
    audience: NbaAudience;
    title: string;
    description: string;
    buttonText: string;
    mode: NbaOverrideMode;
    link: string;
    popupMessage: string;
    publishMode: NbaPublishMode;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
}

export interface NbaFallbackMessage {
    title: string;
    description: string;
    buttonText: string;
    link: string;
}

export interface NbaScenario {
    id: Exclude<NbaScenarioId, 'fallback' | 'override'>;
    type: Exclude<NbaPreviewType, 'override' | 'fallback'>;
    title: string;
    description: string;
    buttonText: string;
    redirect: string;
}

export const NBA_AUDIENCE_OPTIONS: { value: NbaAudience; label: string; description: string; icon: string }[] = [
    { value: 'all', label: 'All Users', description: 'Every mentor on the platform', icon: 'fa-users' },
    {
        value: 'brightside',
        label: 'Brightside-led',
        description: 'Mentors on Brightside-led projects',
        icon: 'fa-building'
    },
    {
        value: 'partner',
        label: 'Partner-led',
        description: 'Mentors on partner-led projects',
        icon: 'fa-handshake-o'
    }
];

export const NBA_OVERRIDE_MODE_OPTIONS: { value: NbaOverrideMode; label: string; description: string; icon: string }[] = [
    { value: 'link', label: 'Redirect to link', description: 'Clicking the button takes the mentor to a page.', icon: 'fa-link' },
    {
        value: 'popup',
        label: 'Show message popup',
        description: 'Clicking the button opens a popup with your message — no redirect.',
        icon: 'fa-comment'
    }
];

export const NBA_PUBLISH_MODE_OPTIONS: { value: NbaPublishMode; label: string; description: string; icon: string }[] = [
    { value: 'now', label: 'Publish Now', description: 'Goes live as soon as you save.', icon: 'fa-bolt' },
    { value: 'schedule', label: 'Schedule', description: 'Goes live at a future date/time.', icon: 'fa-calendar' }
];

export const NBA_SCENARIOS: NbaScenario[] = [
    {
        id: 'step1',
        type: 'step',
        title: 'Complete training',
        description: 'Learn how to mentor confidently on Brightside, including high-quality mentoring, safeguarding and using the platform.',
        buttonText: 'Complete training',
        redirect: '/mentor-hub/training'
    },
    {
        id: 'step2',
        type: 'step',
        title: 'Complete DBS check',
        description: "We'll ask for ID and run a DBS (Disclosure and Barring Service) check",
        buttonText: 'Complete DBS check',
        redirect: '/mentor-hub/training'
    },
    {
        id: 'step3',
        type: 'step',
        title: 'Complete profile',
        description: 'Share your experience, skills and interests to help young people understand how you can support them.',
        buttonText: 'Complete profile',
        redirect: '/mentor-hub/training'
    },
    {
        id: 'project',
        type: 'project',
        title: 'Explore Programmes',
        description: 'Find a programme and join to start mentoring students.',
        buttonText: 'Explore Programmes',
        redirect: '/mentor-hub/projects'
    }
];

export const NBA_SCENARIO_OPTIONS: { value: NbaScenarioId; label: string }[] = [
    { value: 'step1', label: 'Step 1: Training not completed' },
    { value: 'step2', label: 'Step 2: DBS check not completed' },
    { value: 'step3', label: 'Step 3: Profile not completed' },
    { value: 'project', label: 'Explore / Sign up to a programme' },
    { value: 'fallback', label: 'All actions complete (fallback message)' },
    { value: 'override', label: 'Announcement (not active)' }
];

export function createEmptyAnnouncement(audience: NbaAudience): NbaAnnouncement {
    return {
        audience,
        title: '',
        description: '',
        buttonText: '',
        mode: 'link',
        link: '',
        popupMessage: '',
        publishMode: 'now',
        startDate: '',
        startTime: '09:00',
        endDate: '',
        endTime: '23:59'
    };
}

export const DEFAULT_FALLBACK: NbaFallbackMessage = {
    title: 'Keep going!',
    description: "You're all caught up! Check back later for new actions and updates.",
    buttonText: 'Explore Mentor Hub',
    link: '/mentor-hub/projects'
};
